import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react'

type ResizeLogValue = string | number | boolean | null | undefined
export type PointerResizeLogRecord = { event: string } & Record<string, ResizeLogValue>

export interface PointerResizeBounds {
  min: number
  max: number
}

export interface UsePointerResizeOptions {
  /** Current pane width; captured as the drag origin on pointerdown. */
  width: number
  /** Receives the clamped width on every pointermove of an active drag. */
  onWidthChange: (width: number) => void
  /** Read at every pointermove so bounds that depend on layout stay current. */
  getBounds: () => PointerResizeBounds
  /** While true, pointerdown is ignored and any active drag ends with `disabledReason`. */
  disabled?: boolean
  disabledReason?: string
  /** Structured logger; receives `resize-start` and `resize-end` records. */
  log: (record: PointerResizeLogRecord) => void
  /** Called once per drag, after teardown, with the final clamped width and end reason. */
  onResizeEnd?: (width: number, reason: string) => void
}

export function clampWidth(width: number, { min, max }: PointerResizeBounds): number {
  return Math.min(Math.max(width, min), Math.max(min, max))
}

/**
 * Pointer-captured horizontal pane resizing for a pane whose right edge is the
 * drag handle (dragging right grows it).
 *
 * Every end path — pointerup, pointercancel, lostpointercapture, window blur,
 * `disabled` flipping true, and unmount — runs one idempotent teardown that
 * RESTORES the body cursor/userSelect captured at drag start and releases
 * pointer capture, so a release outside the window or a mid-drag unmount can
 * never strand a global `col-resize` cursor.
 */
export function usePointerResize({
  width,
  onWidthChange,
  getBounds,
  disabled = false,
  disabledReason = 'disabled',
  log,
  onResizeEnd,
}: UsePointerResizeOptions): { onPointerDown: (event: ReactPointerEvent<Element>) => void } {
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)
  const prevBodyCursorRef = useRef('')
  const prevBodyUserSelectRef = useRef('')
  const pointerIdRef = useRef<number | null>(null)
  const handleElRef = useRef<Element | null>(null)
  const latestWidthRef = useRef(0)
  // Latest callbacks without re-subscribing the window listeners on each render.
  const callbacksRef = useRef({ onWidthChange, getBounds, log, onResizeEnd })
  callbacksRef.current = { onWidthChange, getBounds, log, onResizeEnd }

  const endResize = useCallback((reason: string) => {
    if (!isResizingRef.current) return
    isResizingRef.current = false
    const restoredCursor = prevBodyCursorRef.current
    document.body.style.cursor = restoredCursor
    document.body.style.userSelect = prevBodyUserSelectRef.current
    prevBodyCursorRef.current = ''
    prevBodyUserSelectRef.current = ''
    const pointerId = pointerIdRef.current
    const el = handleElRef.current
    if (pointerId !== null && el?.hasPointerCapture?.(pointerId)) {
      try {
        el.releasePointerCapture(pointerId)
      } catch {
        // capture may already be released by the browser (pointerup/cancel)
      }
    }
    pointerIdRef.current = null
    handleElRef.current = null
    const { log: emit, onResizeEnd: onEnd } = callbacksRef.current
    // Log the OUTCOME: the cursor value restored ('' = system default) and
    // whether a recovery path (anything but pointerup) ended the drag.
    emit({
      event: 'resize-end',
      reason,
      interrupted: reason !== 'pointerup',
      restoredCursor: restoredCursor === '' ? 'default' : restoredCursor,
      endWidth: latestWidthRef.current,
    })
    onEnd?.(latestWidthRef.current, reason)
  }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<Element>) => {
    // Reject secondary buttons, non-primary pointers, and a second pointerdown
    // mid-drag (which would capture the already-'col-resize' cursor as "prior").
    if (disabled || isResizingRef.current || event.button !== 0 || !event.isPrimary) return
    event.preventDefault()
    isResizingRef.current = true
    startXRef.current = event.clientX
    startWidthRef.current = width
    latestWidthRef.current = width
    prevBodyCursorRef.current = document.body.style.cursor
    prevBodyUserSelectRef.current = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const el = event.currentTarget
    handleElRef.current = el
    // Pin the drag to the initiating pointer whether or not capture succeeds;
    // capture only keeps events flowing once the pointer leaves the handle.
    pointerIdRef.current = event.pointerId
    try {
      el.setPointerCapture(event.pointerId)
    } catch {
      // capture unsupported or refused — the window listeners still see the pointer
    }
    callbacksRef.current.log({ event: 'resize-start', pointerId: event.pointerId, startWidth: width })
  }, [disabled, width])

  useEffect(() => {
    // Only the pointer that started the drag may move/end it.
    const isActivePointer = (event: PointerEvent) => event.pointerId === pointerIdRef.current

    const handlePointerMove = (event: PointerEvent) => {
      if (!isResizingRef.current || !isActivePointer(event)) return
      const { getBounds: bounds, onWidthChange: apply } = callbacksRef.current
      const next = clampWidth(startWidthRef.current + event.clientX - startXRef.current, bounds())
      latestWidthRef.current = next
      apply(next)
    }
    const endOn = (reason: string) => (event: PointerEvent) => {
      if (isActivePointer(event)) endResize(reason)
    }
    const handlePointerUp = endOn('pointerup')
    const handlePointerCancel = endOn('pointercancel')
    const handleLostCapture = endOn('lostpointercapture')
    // Focus loss (alt-tab / OS gesture / devtools) can swallow the release.
    const handleWindowBlur = () => endResize('blur')

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerCancel)
    window.addEventListener('lostpointercapture', handleLostCapture)
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerCancel)
      window.removeEventListener('lostpointercapture', handleLostCapture)
      window.removeEventListener('blur', handleWindowBlur)
      endResize('effect-cleanup')
    }
  }, [endResize])

  // The handle may unmount when disabled (e.g. mobile mode); end explicitly so
  // the cursor resets even if lostpointercapture is never delivered.
  useEffect(() => {
    if (disabled) endResize(disabledReason)
  }, [disabled, disabledReason, endResize])

  return { onPointerDown }
}
